import {Box, Button, Divider, Grid, LinearProgress, Paper, TextField, Typography, Chip} from "@mui/material";
import React, {useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {format} from 'date-fns';
import {DataGrid, GridRenderCellParams} from "@mui/x-data-grid";
import {ActionColumn, Column, columnStyle, DateColumn} from "../../../../common/Columns";
import NoRowsOverlay from "../../../../common/NoRowsOverlay";
import {agreementService, AGREEMENT_STATUS, Agreement} from "../../../../api/services/agreement";
import useSignature from "../../../../api/services/signature";
import {LocalizationProvider, MobileDatePicker} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import kkLocale from "date-fns/locale/kk";
import ruLocale from "date-fns/locale/ru";
import {AuctionFile} from "../../../../types/auction";
import {publicService} from "../../../../api/services/public";
import {AxiosError} from "axios";
import {useSnack} from "../../../../utils/useSnack";
import {PlanState} from "../../../../utils/enums";

export default function Agreements({userRole, contract, participant, auctionId, companyId}: {
    userRole: string,
    contract: { number: string, status: string, customer: string, supplier: string }
    participant?: boolean
    auctionId: number
    companyId: number | undefined
}) {
    const {t, i18n} = useTranslation();
    const [warrant, setWarrant] = useState('');
    const [dueDate, setDueDate] = useState<Date | null>(null);
    const [agreements, setAgreements] = useState<Agreement[]>([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const {signXML} = useSignature();
    const {snack} = useSnack();

    const fetchAgreements = async () => {
        setLoading(true);
        try {
            if (!companyId) {
                return
            }
            const response = await agreementService.getAgreements(companyId, auctionId);
            if (response.status === 'success' && response.data) {
                setAgreements(response.data);
            }
        } catch (error) {
            console.error("Error fetching agreements:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAgreements();
    }, [auctionId, companyId]);

    const downloadFile = async (agreementId: number) => {
        const errorText = t("agreements.errors.downloadFailed");

        try {

            agreementService.downloadFile(companyId!!, agreementId);

        } catch (error) {
            const err = error as AxiosError;
            snack(errorText, "error");
        }
    };

    const actions = [
        {
            label: t("agreements.actions.sign"),
            callback: (row: any) => {
                // Логика удаления плана. В данном примере используется подтверждение удаления.
                handleSignAgreement(row.id);
            },
            predicate: (row: any) => {
                // Здесь можно задать условие доступности действия для конкретной записи.
                // Например, вернуть true, если удаление разрешено для данного плана.
                return (row.status === 'SIGN_CUSTOMER' && row.customerId === companyId) || (row.status === 'SIGN_SUPPLIER' && row.supplierId === companyId);
            }
        },
        {
            label: t("agreements.actions.reject"),
            callback: (row: any) => {
                // Логика удаления плана. В данном примере используется подтверждение удаления.
                handleReject(row.id);
            },
            predicate: (row: Agreement) => {
                // Здесь можно задать условие доступности действия для конкретной записи.
                // Например, вернуть true, если удаление разрешено для данного плана.
                return (row.status === 'SING_CUSTOMER' && row.customerId === companyId) || (row.status === 'SIGN_SUPPLIER' && row.supplierId === companyId);
            }
        }]

    const handleSignAgreement = async (id: number) => {
        const errorText = t("agreements.errors.downloadFailed");
        const response = await agreementService.getPassport(companyId!!, id)
        try {
            if (response.status === 'success' && response.data) {
                const passport = response.data.passport
                const signedXml = await signXML('SIGN', passport)
                const formData = new FormData();
                formData.append("signature", signedXml.body!.result[0]);
                const signResponse = await agreementService.signPassport(companyId!!, id, formData)
                if (signResponse.status === 'success') {
                    fetchAgreements()
                }
            } else {
                snack(t("agreements.errors.general"), "error");
            }
        } catch (error) {
            const err = error as AxiosError;
            snack(errorText, "error");
        }
    }
    const handleReject = async (agreementId: number) => {
        console.log(`Rejecting ${agreementId}`)
    }

    const handleCreateAgreement = async () => {
        if (agreements.length === 0 && !participant && warrant.trim() && dueDate) {
            setCreating(true);
            try {
                if (!companyId) {
                    return
                }
                // Include dueDate in the request
                const response = await agreementService.createAgreement(companyId, auctionId, warrant, dueDate);
                if (response.status === 'success' && response.data) {
                    // Add the new agreement to the list
                    fetchAgreements()
                    // Clear the fields
                    setWarrant('');
                    setDueDate(null);
                }
            } catch (error) {
                console.error("Error creating agreement:", error);
            } finally {
                setCreating(false);
            }
        }
    };

    return (
        <Box sx={{p: 3}}>
            <Typography variant="h6" gutterBottom>
                {t("agreements.title")}
            </Typography>
            <Typography paragraph>
                {t("agreements.instructions.intro")}
            </Typography>
            <Box component="ol" sx={{pl: 3}}>
                <li><Typography>{t("agreements.instructions.step1")}</Typography></li>
                <li><Typography>{t("agreements.instructions.step2")}</Typography></li>
                <li><Typography>{t("agreements.instructions.step3")}</Typography></li>
                <li><Typography>{t("agreements.instructions.step4")}</Typography></li>
                <li><Typography>{t("agreements.instructions.step5")}</Typography></li>
                <li><Typography>{t("agreements.instructions.step6")}</Typography></li>
            </Box>

            <Divider sx={{my: 4}}/>

            {!participant && agreements.length === 0 &&
                <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={i18n.language == "kk" ? kkLocale : ruLocale}>
                    <Box sx={{mt: 2}}>
                        <Box
                            component={Button}
                            variant="contained"
                            size={'small'}
                            marginTop={{xs: 2, md: 0}}
                            onClick={handleCreateAgreement}
                            disabled={creating || !warrant.trim() || !dueDate || agreements.length > 0}
                        >
                            {creating ? t("agreements.buttons.creating") : t("agreements.buttons.createAgreement")}
                        </Box>
                        <Grid item xs={3} sx={{mt: 2}}>
                            <MobileDatePicker
                                label={t("agreements.form.dueDate")}
                                value={dueDate}
                                onChange={(newValue: Date | null) => setDueDate(newValue)}
                                inputFormat="dd/MM/yyyy"
                                disabled={creating}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        size="small"
                                        color="primary"
                                        required
                                        error={!dueDate}
                                        helperText={!dueDate ? t("agreements.form.required") : ""}
                                        sx={{mb: 2}}
                                    />
                                )}
                            />
                            <TextField
                                fullWidth color="primary" type="text" size="small"
                                label={t("agreements.form.agreementText")}
                                value={warrant}
                                onChange={(e) => setWarrant(e.target.value)}
                                multiline
                                rows={4}
                                disabled={creating}
                                required
                            />
                        </Grid>
                    </Box>
                </LocalizationProvider>
            }

            <Typography variant="h6" gutterBottom sx={{mt: 4}}>
                {t("agreements.documentsTitle")}
            </Typography>

            <div style={{height: 400, width: '100%', marginTop: 16}}>
                <DataGrid
                    density="standard"
                    components={{NoRowsOverlay: () => <NoRowsOverlay/>}}
                    rows={agreements}
                    loading={loading}
                    initialState={{
                        sorting: {
                            sortModel: [{field: 'createdAt', sort: 'desc'}],
                        },
                    }}
                    pageSize={5}
                    page={0}
                    rowsPerPageOptions={[5, 10]}
                    columns={[
                        ActionColumn(100, columnStyle, "cabinet.plans", actions),
                        {
                            field: 'id',
                            headerName: 'ID',
                            width: 70,
                            renderCell: (params) => (
                                <div style={columnStyle}>
                                    {params.value}
                                </div>
                            ),
                        },
                        {
                            field: 'status',
                            headerName: t("agreements.columns.status"),
                            width: 250,
                            renderCell: (params) => {
                                let color = 'default';
                                let label = params.value;

                                switch (params.value) {
                                    case AGREEMENT_STATUS.CREATION:
                                        color = 'info';
                                        label = t("agreements.status.creation");
                                        break;
                                    case AGREEMENT_STATUS.SIGN_CUSTOMER:
                                        color = 'warning';
                                        label = t("agreements.status.signCustomer");
                                        break;
                                    case AGREEMENT_STATUS.SIGN_SUPPLIER:
                                        color = 'primary';
                                        label = t("agreements.status.signSupplier");
                                        break;
                                    case AGREEMENT_STATUS.REJECT:
                                        color = 'error';
                                        label = t("agreements.status.rejected");
                                        break;
                                }

                                return (
                                    <Chip
                                        label={label}
                                        color={color as any}
                                        size="small"
                                    />
                                );
                            },
                        },
                        {
                            field: 'supplierName',
                            headerName: t("agreements.columns.supplier"),
                            width: 200,
                            renderCell: (params) => (
                                <div style={columnStyle}>
                                    {params.value}
                                </div>
                            ),
                        },
                        {
                            field: 'customerName',
                            headerName: t("agreements.columns.customer"),
                            width: 200,
                            renderCell: (params) => (
                                <div style={columnStyle}>
                                    {params.value}
                                </div>
                            ),
                        },
                        {
                            field: 'createdAt',
                            headerName: t("agreements.columns.createdAt"),
                            width: 150,
                            renderCell: (params) => (
                                <div style={columnStyle}>
                                    {format(new Date(params.value), 'dd.MM.yyyy')}
                                </div>
                            ),
                        },
                        {
                            field: 'dueDate',
                            headerName: t("agreements.columns.dueDate"),
                            width: 150,
                            renderCell: (params) => (
                                <div style={columnStyle}>
                                    {params.value ? format(new Date(params.value), 'dd.MM.yyyy') : '-'}
                                </div>
                            ),
                        },
                        {
                            field: 'download',
                            headerName: t("agreements.columns.download"),
                            width: 150,
                            renderCell: (params: GridRenderCellParams<any, any, any>) => (
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={(event) => downloadFile(params.row.id)}
                                >
                                    {t("agreements.buttons.download")}
                                </Button>
                            ),
                        }
                    ]}
                />
            </div>
        </Box>
    )
}
